/**
 * (post) content formatting
 */

import React from "react"
import HtmlToReact, { Parser as HtmlToReactParser } from "html-to-react"
import { GatsbyImage } from "gatsby-plugin-image"
import styled, { css } from "styled-components"

// components
import { Gap, Row, Col, RoundedImage, RoundWrapper } from "../components"
import ProcedureWidget from "../components/ProcedureWidget"
import Youtube from "../components/Youtube"

const parseStyles = styles =>
  styles
    .split(";")
    .filter(style => style.split(":")[0] && style.split(":")[1])
    .map(style => [
      style
        .split(":")[0]
        .trim()
        .replace(/-./g, c => c.substr(1).toUpperCase()),
      style.split(":")[1].trim(),
    ])
    .reduce(
      (styleObj, style) => ({
        ...styleObj,
        [style[0]]: style[1],
      }),
      {}
    )

export const replaceShortCodes = content => {
  let searchIndex = 0
  while (content.indexOf("[video ", searchIndex) !== -1) {
    const startIndex = content.indexOf("[video ", searchIndex)
    const endIndex = content.indexOf("]", startIndex) + 1
    const videoCodeIndex = content.indexOf('code="', startIndex) + 6
    const videoCode = content.substring(videoCodeIndex, videoCodeIndex + 11)
    content = `${content.substring(0, startIndex)}
    <youtubewidget data-video-id="${videoCode}"></youtubewidget>
    ${content.substring(endIndex)}
    `
    searchIndex = endIndex
  }
  searchIndex = 0
  while (content.indexOf("[procedure ", searchIndex) !== -1) {
    const startIndex = content.indexOf("[procedure ", searchIndex)
    const endIndex = content.indexOf("]", startIndex) + 1
    const idIndex = content.indexOf('id="', startIndex) + 4
    const id = content.substring(idIndex, idIndex + 24)

    content = `${content.substring(0, startIndex)}
      <procedurewidget data-id="${id}"></procedurewidget>
      ${content.substring(endIndex)}
      `
    searchIndex = endIndex
  }

  return content
}

// CONTENT parsing
const htmlToReactParser = new HtmlToReactParser()
const processNodeDefinitions = new HtmlToReact.ProcessNodeDefinitions(React)
const getProcessingInstruction = (images, procedures = []) => [
  {
    // replace p tag with custom component
    shouldProcessNode(node) {
      return node.name === "p"
    },
    processNode(node, children, index) {
      return (
        <div
          className="p"
          key={index}
          style={
            node.attribs.style ? parseStyles(node.attribs.style) : undefined
          }
        >
          {children}
        </div>
      )
    },
  },
  {
    // Custom <img> processing
    shouldProcessNode(node) {
      return node.name === "img"
    },
    processNode(node, children, index) {
      const foundImage = images.find(
        image =>
          node.attribs.src &&
          node.attribs.src.indexOf(image.relativePath) !== -1
      )
      if (foundImage && foundImage.contentImage) {
        const noMask = node.attribs.class && node.attribs.class === "nomask"
        const ImageComponent = noMask ? GatsbyImage : RoundedImage


        // console.log('foundImage.contentImage.fluid', foundImage.contentImage.fluid)

        return (
          <Col maxWidth="587px" key={node.attribs.src + index}>
            <Gap gap="24px" mobileGap="16px" />
            <ImageComponent
              alt={node.attribs.alt}
              image={
                !noMask
                  ? foundImage.contentImage.fluid
                  : foundImage.contentImage.fluidNoCrop
              }
            />
            <Gap gap="24px" mobileGap="16px" />
          </Col>
        )
      }
      return <img {...node.attribs} />
    },
  },
  {
    // parse youtube videos
    shouldProcessNode(node) {
      return node.name === "youtubewidget"
    },
    processNode(node, children, index) {
      // console.log('node', node)
      return (
        <Col
          maxWidth="587px"
          position="relative"
          key={node.attribs["data-video-id"] + index}
        >
          <Youtube videoId={node.attribs["data-video-id"]} />
        </Col>
      )
    },
  },
  {
    // parse youtube videos
    shouldProcessNode(node) {
      return node.name === "procedurewidget"
    },
    processNode(node, children, index) {
      const p = procedures.find(p => p._id === node.attribs["data-id"])
      return <ProcedureWidget procedure={p} key={p._id + index} />
    },
  },
  {
    // Anything else
    shouldProcessNode(node) {
      return true
    },
    processNode: processNodeDefinitions.processDefaultNode,
  },
]

export const enhanceContent = ({ content, contentImages, procedures }) => {
  // replacing shortCodes with custom tags
  const postContent = replaceShortCodes(content)

  // replace tags with react components
  const contentReactElement = htmlToReactParser.parseWithInstructions(
    postContent,
    () => true,
    getProcessingInstruction(contentImages, procedures)
  )

  return contentReactElement
}
